import { useContext } from "react";

import { createReference } from "@medplum/core";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { rem } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { Loading } from "@/components/Loading";
import { EncounterContext } from "@/contexts/encounter";
import { PatientContext } from "@/contexts/patient";
import { PhysicalExamTypes } from "@/fhir/enums/physical-exams";
import { GynecologicalPhysicalExamsQuestionnaires } from "@/fhir/enums/questionnaires";
import usePhysicalExamInitialValues from "@/physical-exams/hooks/use-physical-exam-initial-values";
import usePhysicalExamQuestionnaire from "@/physical-exams/hooks/use-physical-exam-questionnaire";
import usePhysicalExamUnsavedChanges from "@/physical-exams/hooks/usePhysicalExamUnsavedChanges";
import PhysicalExamHeader from "@/physical-exams/PhysicalExamHeader";
import PhysicalExamList from "@/physical-exams/PhysicalExamList";
import {
  amendPhysicalExamQuestionnaireResponse,
  createPhysicalExamQuestionnaireResponse,
  getMainQuestionnaireUrlFromEncounter,
} from "@/physical-exams/services/physical-exams";
import { PhysicalExamProps } from "@/physical-exams/types/physical-exams";
import { PERINEUM_AND_EXTERNAL_GENITALIA_FORM_INITIAL_VALUES } from "@/physical-exams/types/postpartum-perineum-and-external-genitalia";
import {
  PhysicalExamForm,
  PhysicalExamFormSchema,
} from "@/physical-exams/zod-schemas/physical-exams";
import { useTemporarySubmit } from "@/utils/hooks";

const GynecologicalPerineumAndExternalGenitaliaPhysicalExam = ({
  url,
  submitTrigger,
  editable,
  isAmendment,
}: PhysicalExamProps) => {
  const { patient } = useContext(PatientContext);
  const { encounter } = useContext(EncounterContext);
  const { sections, questionnaire } = usePhysicalExamQuestionnaire(
    GynecologicalPhysicalExamsQuestionnaires.PerineumAndExternalGenitaliaPhysicalExam,
  );

  const medplum = useMedplum();
  const form = useForm<PhysicalExamForm>({
    initialValues: PERINEUM_AND_EXTERNAL_GENITALIA_FORM_INITIAL_VALUES,
    validate: zodResolver(PhysicalExamFormSchema),
  });

  const formSetValues = form.setValues;
  const [initialValues, questionnaireResponse, isLoading] = usePhysicalExamInitialValues(
    questionnaire?.id,
    encounter,
    formSetValues,
    PERINEUM_AND_EXTERNAL_GENITALIA_FORM_INITIAL_VALUES,
  );
  const { markAsSaved } = usePhysicalExamUnsavedChanges(url, form, initialValues);

  const practitioner = medplum.getProfile() as Practitioner;

  const handleSubmit = async (values: PhysicalExamForm) => {
    const mainQuestionnaireUrl = getMainQuestionnaireUrlFromEncounter(encounter!);

    if (isAmendment && questionnaireResponse) {
      await amendPhysicalExamQuestionnaireResponse(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    } else {
      await createPhysicalExamQuestionnaireResponse(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    }
    markAsSaved();
  };

  useTemporarySubmit<PhysicalExamForm>(form, submitTrigger, handleSubmit);

  if (!patient?.id || isLoading) return <Loading height={rem(96)} />;

  return (
    <form
      onSubmit={form.onSubmit((values, _) => handleSubmit(values))}
      data-testid="physical-exam-gynecological-perineum-and-external-genitalia-form"
    >
      <PhysicalExamHeader
        url={url}
        form={form}
        sections={sections}
        canDelete={editable}
        clientId={patient!.id!}
        showQuickSelection={editable}
        title="Perineum & External Genitalia"
        value={PhysicalExamTypes.GynecologicalPerineumAndExternalGenitalia}
      >
        <PhysicalExamList
          form={form}
          sections={sections}
          editable={editable}
          questionnaire={questionnaire}
        />
      </PhysicalExamHeader>
    </form>
  );
};

export default GynecologicalPerineumAndExternalGenitaliaPhysicalExam;
